// Single owner of the wrap-hide stylesheets (`#lay-hide-wrap-css` and the
// per-overlay variants) that briefly hide .lay-content during navigation
// transitions so newly-inserted markup doesn't flash before the transition-in
// animation runs.
//
// Why this lives in one module: each transition both _inserts_ and later
// _removes_ the stylesheet, plus schedules a safety-net `setTimeout` to remove
// it as a fallback. When a new transition starts within the lifetime of a
// previous safety net, the old timer can rip the new stylesheet off
// mid-flight — that's the "content flash" race users hit when clicking
// thumbnails in quick succession. By funnelling every hide / reveal /
// safety-schedule through a single module that owns the timer state, any new
// hide automatically supersedes the previous safety timer.

import { insertWrapHideStyleSheet, insertWrapHideStyleSheetForPageOverlay, insertWrapHideStyleSheetForProjectOverlay } from '../shared/insertWrapHideStylesheets'
import { onlyRemovePageOverlayHideStyleSheet, onlyRemoveProjectoverlayHideStyleSheet, removeWrapHideStyleSheet } from '../shared/removeWrapHideStylesheets'
import { SCOPES } from '../../scope/scope'

// A single timer is sufficient: the safety net is "if anything got stuck,
// reveal everything". We never need two pending safety timers concurrently —
// the latest transition wins.
let pendingSafetyTimer = null

const cancelPendingSafetyReveal = function () {
	if (pendingSafetyTimer !== null) {
		clearTimeout(pendingSafetyTimer)
		pendingSafetyTimer = null
	}
}

const hideScope = function (scope) {
	// Any prior safety net is moot: a fresh hide means a fresh transition.
	// Without this cancel, the previous transition's `scheduleSafetyReveal`
	// would fire after our insert and take the new hide-css with it.
	cancelPendingSafetyReveal()
	switch (scope) {
		case SCOPES.PROJECT_OVERLAY:
			insertWrapHideStyleSheetForProjectOverlay()
			break
		case SCOPES.PAGE_OVERLAY:
			insertWrapHideStyleSheetForPageOverlay()
			break
		case SCOPES.NORMAL_PAGE:
		default:
			insertWrapHideStyleSheet()
			break
	}
}

const showScope = function (scope) {
	// Cleanly revealing this scope retires whatever safety net was scheduled.
	cancelPendingSafetyReveal()
	switch (scope) {
		case SCOPES.PROJECT_OVERLAY:
			onlyRemoveProjectoverlayHideStyleSheet()
			break
		case SCOPES.PAGE_OVERLAY:
			onlyRemovePageOverlayHideStyleSheet()
			break
		case SCOPES.NORMAL_PAGE:
		default:
			removeWrapHideStyleSheet()
			break
	}
}

const showAllScopes = function () {
	cancelPendingSafetyReveal()
	removeWrapHideStyleSheet()
	onlyRemoveProjectoverlayHideStyleSheet()
	onlyRemovePageOverlayHideStyleSheet()
}

// Safety net: if the transition-in animation's `begin` callback never fires
// (e.g. `waitForInviewImagesLoaded` never resolves because a network image
// 404s and the await falls into a no-op path), we'd be left with hide-css
// stuck on the page forever. Schedule a delayed full reveal as fallback.
// Replacing any previously-scheduled timer is intentional — each transition's
// safety net supersedes the prior one, and `hideScope` cancels any pending
// timer up-front for the same reason.
const scheduleSafetyReveal = function (delayMs) {
	cancelPendingSafetyReveal()
	pendingSafetyTimer = setTimeout(function () {
		pendingSafetyTimer = null
		removeWrapHideStyleSheet()
		onlyRemoveProjectoverlayHideStyleSheet()
		onlyRemovePageOverlayHideStyleSheet()
	}, delayMs)
}

const visibility_gate = {
	hideScope: hideScope,
	showScope: showScope,
	showAllScopes: showAllScopes,
	scheduleSafetyReveal: scheduleSafetyReveal,
	cancelPendingSafetyReveal: cancelPendingSafetyReveal,
}

export default visibility_gate
